import { create } from 'zustand';

/**
 * Who was on the call, carried from the call bar to the form it just opened.
 *
 * The job form seeds its customer section from the caller when there is no
 * contact on file. It normally reads that off the live call itself — but a
 * caller who hangs up in the second between pressing "Create job" and the form
 * mounting is gone from the call store before anything can ask, and the agent
 * is left retyping an identity the phone system had displayed to them a moment
 * earlier. That is the exact case the prefill exists for.
 *
 * So the call bar leaves it here on its way out.
 *
 * NOT the URL, which is where this started: a query string reaches browser
 * history, the referrer on any outbound link, proxy and server access logs, and
 * every screenshot and pasted link. A customer's name and phone number have no
 * business in any of them. This is in memory, for one navigation, and never
 * persisted — a reload legitimately loses it, because a reload has no call bar
 * press behind it.
 *
 * Keyed by the browser's LOCAL leg id rather than the Telnyx call-control id,
 * because a browser-placed outbound call never gets a Telnyx id at all — so
 * keying on that gave no handoff to precisely the calls that most need one. The
 * local id is opaque and names a leg, not a person, so it can travel in the URL
 * while the identity it points at stays here.
 *
 * One slot, not a map: the flow is a single press followed by a single
 * navigation, so there is never a second handoff in flight, and a slot cannot
 * grow the way a keyed cache would. It is stamped with the call it describes
 * and only ever handed back for that same call, so a form opened later for a
 * different call gets nothing rather than the wrong person.
 */
export interface CallerHandoff {
  /** The browser's own id for the call leg this identity came from. */
  callId: string;
  name: string | null;
  phone: string | null;
}

interface CallerHandoffState {
  handoff: CallerHandoff | null;
  remember: (handoff: CallerHandoff) => void;
  /** The identity for this call, or null if the slot holds someone else's. */
  read: (callId: string | null | undefined) => CallerHandoff | null;
}

export const useCallerHandoffStore = create<CallerHandoffState>()((set, get) => ({
  handoff: null,
  remember: (handoff) => set({ handoff }),
  read: (callId) => {
    if (!callId) return null;
    const held = get().handoff;
    return held && held.callId === callId ? held : null;
  },
}));
